<template>
    <div class="md:w-3/4 lg:w-3/5 xl:w-1/2 mx-auto">
        <form
            ref="form"
            :action="`/products/${product.slug}/reviews`"
            method="POST"
            @submit.prevent="onSubmit(form)"
        >
            <div
                class="mt-4 bg-white px-2 py-5 px-4 sm:rounded-lg sm:shadow-md sm:border sm:border-gray-200 sm:p-6"
            >
                <input type="hidden" name="_token" :value="csrf" />
                <input
                    type="hidden"
                    name="post_anyway"
                    :value="inputs.postAnyway"
                />
                <!-- Names the review these words belong to, so they update a rating left
                     minutes ago rather than storing a second review. -->
                <input
                    type="hidden"
                    name="review_binding"
                    :value="reviewBinding"
                />
                <!-- Tells the server whose words these are when the customer has no login
                     and no cookie. -->
                <input type="hidden" name="grant" :value="grant" />

                <div>
                    <label
                        for="rating"
                        class="block text-sm font-medium text-gray-700"
                    >
                        Rating
                    </label>
                    <input type="hidden" name="rating" :value="inputs.rating" />

                    <RatingStars
                        :model-value="inputs.rating"
                        @update:model-value="inputs.rating = $event"
                    />

                    <p
                        v-if="hasError(errors, 'rating')"
                        class="mt-2 text-sm text-red-600"
                        id="rating-error"
                    >
                        {{ error(errors, "rating") }}
                    </p>

                    <p
                        v-if="ratingConfirmation"
                        data-test="rating-confirmation"
                        class="mt-3 rounded-md border border-green-100 bg-green-50 p-3 text-sm text-green-800"
                    >
                        {{ ratingConfirmation }}
                    </p>
                </div>

                <div class="mt-6">
                    <label
                        for="body"
                        class="block text-sm font-medium text-gray-700"
                    >
                        Write a review
                        <span class="font-normal text-gray-500"
                            >(optional)</span
                        >
                    </label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <textarea
                            v-model="inputs.body"
                            :ref="refs.body"
                            id="body"
                            name="body"
                            rows="10"
                            :class="[
                                hasError(errors, 'body')
                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                    : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                            ]"
                            :aria-invalid="
                                hasError(errors, 'body') ? 'true' : 'false'
                            "
                            :aria-describedby="
                                hasError(errors, 'body') ? 'body-error' : ''
                            "
                        />
                        <div
                            v-if="hasError(errors, 'body')"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                        >
                            <ExclamationCircleIcon
                                class="h-5 w-5 text-red-500"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                    <p
                        v-if="hasError(errors, 'body')"
                        class="mt-2 text-sm text-red-600"
                        id="body-error"
                    >
                        {{ error(errors, "body") }}
                    </p>
                </div>

                <div class="mt-6">
                    <ReviewMediaCapture
                        :account-hash="imagesAccountHash"
                        :csrf="csrf"
                        :initial-ids="initialMediaIds"
                        :initial-video-id="initialVideoId"
                        :maximum-photos="maximumPhotos"
                        :maximum-video-bytes="maximumVideoBytes"
                        :thumbnail-variant="imagesThumbnailVariant"
                        :video-allowed="videoAllowed"
                        :video-clip-seconds="videoClipSeconds"
                        @photos-changed="mediaIds = $event"
                        @photos-uploading="uploadingPhoto = $event"
                        @video-changed="videoId = $event"
                        @video-uploading="uploadingVideo = $event"
                    />
                    <input
                        v-for="id in mediaIds"
                        :key="id"
                        type="hidden"
                        name="media[]"
                        :value="id"
                    />
                    <input
                        v-if="videoId"
                        type="hidden"
                        name="video"
                        :value="videoId"
                    />
                    <p
                        v-if="hasError(errors, 'media')"
                        class="mt-2 text-sm text-red-600"
                        id="media-error"
                    >
                        {{ error(errors, "media") }}
                    </p>
                    <p
                        v-if="hasError(errors, 'video')"
                        class="mt-2 text-sm text-red-600"
                        id="video-error"
                    >
                        {{ error(errors, "video") }}
                    </p>
                </div>

                <div class="mt-6">
                    <label
                        for="title"
                        class="block text-sm font-medium text-gray-700"
                    >
                        Title your review
                        <span class="font-normal text-gray-500"
                            >(optional)</span
                        >
                    </label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="inputs.title"
                            :ref="refs.title"
                            id="title"
                            name="title"
                            type="text"
                            :class="[
                                hasError(errors, 'title')
                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                    : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                            ]"
                            :aria-invalid="
                                hasError(errors, 'title') ? 'true' : 'false'
                            "
                            :aria-describedby="
                                hasError(errors, 'title') ? 'title-error' : ''
                            "
                        />
                        <div
                            v-if="hasError(errors, 'title')"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                        >
                            <ExclamationCircleIcon
                                class="h-5 w-5 text-red-500"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                    <p
                        v-if="hasError(errors, 'title')"
                        class="mt-2 text-sm text-red-600"
                        id="title-error"
                    >
                        {{ error(errors, "title") }}
                    </p>
                </div>

                <div class="mt-6">
                    <label
                        for="name"
                        class="block text-sm font-medium text-gray-700"
                    >
                        Display Name
                        <span class="font-normal text-gray-500"
                            >(optional)</span
                        >
                    </label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="inputs.name"
                            :ref="refs.name"
                            id="name"
                            name="name"
                            type="text"
                            autocomplete="name"
                            :class="[
                                hasError(errors, 'name')
                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                    : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                            ]"
                            :aria-invalid="
                                hasError(errors, 'name') ? 'true' : 'false'
                            "
                            :aria-describedby="
                                hasError(errors, 'name') ? 'name-error' : ''
                            "
                        />
                        <div
                            v-if="hasError(errors, 'name')"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                        >
                            <ExclamationCircleIcon
                                class="h-5 w-5 text-red-500"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                    <p
                        v-if="hasError(errors, 'name')"
                        class="mt-2 text-sm text-red-600"
                        id="name-error"
                    >
                        {{ error(errors, "name") }}
                    </p>
                </div>

                <ReviewLanguageFindings :findings="findings" />
            </div>

            <div
                v-if="bounced"
                class="sm:px-6 pt-6 flex justify-center items-center"
            >
                <button
                    data-test="post-anyway"
                    name="post-anyway"
                    type="button"
                    :disabled="uploading"
                    @click="postAnyway"
                    class="inline-flex justify-center items-center px-6 py-3 border border-amber-200 rounded-md shadow-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Post it anyway
                </button>
            </div>

            <div class="sm:px-6 py-6 flex justify-center items-center">
                <div class="w-2/5 sm:w-1/3 pr-1 sm:pr-4">
                    <a
                        role="button"
                        :href="`/products/${product.slug}/reviews`"
                        class="inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none w-full"
                    >
                        <XIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                        Cancel
                    </a>
                </div>

                <div class="w-3/5 sm:w-2/3 pl-1 sm:pl-4">
                    <!-- Submitting mid-upload drops the file silently: it has no id to post
                         until Cloudflare has its bytes. -->
                    <button
                        name="save"
                        type="submit"
                        :disabled="uploading"
                        class="inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none w-full disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <LoadingIcon
                            v-if="submitted || uploading"
                            class="h-5 w-5 mr-2"
                        />
                        <CheckIcon
                            v-else
                            class="h-5 w-5 mr-2"
                            aria-hidden="true"
                        />
                        {{ saveLabel }}
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { CheckIcon, ExclamationCircleIcon, XIcon } from "@heroicons/vue/solid";
import { recordReviewRating } from "../../api/reviews";
import { useForm } from "../../functions/useForm";
import type { LanguageFinding } from "../../functions/reviewLanguage";
import { useReviewRating } from "../../functions/useReviewRating";
import LoadingIcon from "../LoadingIcon.vue";
import RatingStars from "./RatingStars.vue";
import ReviewLanguageFindings from "./ReviewLanguageFindings.vue";
import ReviewMediaCapture from "./ReviewMediaCapture.vue";

const props = defineProps<{
    csrf?: string;
    errors?: any;
    grant?: string;
    imagesAccountHash?: string;
    imagesThumbnailVariant?: string;
    initialName?: string;
    initialRating?: number | null;
    languageFindings?: LanguageFinding[];
    maximumPhotos?: number;
    maximumVideoBytes?: number;
    old?: any;
    product?: any;
    reviewBinding?: string;
    videoAllowed?: boolean;
    videoClipSeconds?: number;
}>();

const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const form = ref<any>(null);
const refs = {
    name: ref<any>(null),
    title: ref<any>(null),
    body: ref<any>(null),
};
const { filledStars } = useReviewRating();

// Presence, not truthiness: a rejected submission can carry "0" or "" for the rating, and
// discarding those falsy values would silently restore the rating the email link asked for.
// It is redisplayed exactly as sent, because normalising "4.5" to 4 resubmits a rating the
// customer never chose.
const oldRating = oldInput(props.old, "rating");
const initialRating =
    oldRating !== null ? oldRating : (props.initialRating ?? null);

const inputs = reactive({
    rating: initialRating,
    name: oldInput(props.old, "name")
        ? oldInput(props.old, "name")
        : props.initialName,
    title: oldInput(props.old, "title") ? oldInput(props.old, "title") : null,
    body: oldInput(props.old, "body") ? oldInput(props.old, "body") : null,
    // HTMLFormElement.submit() drops the submitter's name and value, so the intent to post
    // through a warning has to travel as a field of its own.
    postAnyway: 0,
});
/**
 * The photo ids a bounced submission carried back.
 *
 * Seeded from old input rather than empty, because a validation failure and the "post it anyway"
 * bounce both return through back()->withInput() and would otherwise drop photos the customer has
 * already uploaded. Read defensively because `media` is only a list when the form itself posted
 * it: a hand-crafted `media=<id>` is flashed back as the bare string it was, and spreading that
 * would render one hidden input per character.
 */
function oldMediaIds(): string[] {
    const media = oldInput(props.old, "media");

    return Array.isArray(media)
        ? media.filter((id): id is string => typeof id === "string")
        : [];
}

const mediaIds = ref<string[]>(oldMediaIds());
// A snapshot: the capture component seeds its strip from this once, and the live list would keep
// moving underneath it.
const initialMediaIds = [...mediaIds.value];
const uploadingPhoto = ref(false);

/**
 * The video id a bounced submission carried back.
 *
 * Seeded from old input for the reason the photo ids are, and it matters more here: re-shooting a
 * minute of video because the title was too long is the kind of thing people abandon over. Only a
 * string when the form itself posted it.
 */
function oldVideoId(): string {
    const video = oldInput(props.old, "video");

    return typeof video === "string" ? video : "";
}

const initialVideoId = oldVideoId();
const videoId = ref<string | null>(initialVideoId || null);
const uploadingVideo = ref(false);

const uploading = computed(() => uploadingPhoto.value || uploadingVideo.value);
const saveLabel = computed(() => {
    if (uploadingVideo.value) {
        return "Finishing your video…";
    }

    return uploadingPhoto.value ? "Finishing your photo…" : "Save";
});

async function postAnyway(): Promise<void> {
    // Save is a submit button and this is not, so the browser's own check on the required
    // fields never runs here.
    if (!form.value?.reportValidity()) {
        return;
    }

    inputs.postAnyway = 1;
    // form.submit() reads the DOM and Vue writes the hidden input on the next flush, so
    // without the tick the form posts the old value and bounces a second time.
    await nextTick();
    onSubmit(form.value);
}

// Filled in by the rating POST below when the page rendered before any review existed, which is
// every first visit from an emailed star link.
const reviewBinding = ref(props.reviewBinding ?? "");
const ratingConfirmation = ref("");

/**
 * Store the rating the emailed link asked for.
 *
 * Posted after mount rather than recorded by the link itself: mail clients and anti-virus
 * scanners prefetch all five star links, and a GET that recorded a rating would fabricate a
 * testimonial from a customer who never clicked.
 */
async function recordRating(): Promise<void> {
    // Without a grant the server cannot say whose rating this is. An already-bound review is
    // deliberately not skipped — a second star click is the customer changing their mind, and
    // recording is idempotent.
    if (!props.grant || filledStars(inputs.rating) === 0) {
        return;
    }

    try {
        const data = await recordReviewRating({
            _token: props.csrf ?? "",
            product_id: props.product?.id,
            rating: inputs.rating,
            grant: props.grant,
        });

        reviewBinding.value = data.reviewBinding;
        ratingConfirmation.value = data.message;
    } catch {
        // The early save is a courtesy: Save still stores whatever they write, so there is
        // nothing here the customer could act on.
    }
}

// Left on screen while the customer edits, as the reference they are correcting against.
const findings = props.languageFindings ?? [];

// Only a warning the server gave can be posted through. Editing any of the three fields the
// verdict was keyed on invalidates it, so the control goes with it.
const bounced = ref((props.languageFindings ?? []).length > 0);

watch(
    () => [inputs.title, inputs.body, inputs.name],
    () => {
        bounced.value = false;
    },
);

/**
 * Put the cursor on the first field the customer has to look at, in the order they are rendered.
 */
function focusTheFirstFieldWithAnError(): void {
    // A rating error is left alone, having no field to focus.
    if (hasError(props.errors, "rating")) {
        return;
    }

    const fields = ["body", "title", "name"] as const;
    const firstWithAnError = fields.find((field) =>
        hasError(props.errors, field),
    );

    refs[firstWithAnError ?? "body"].value.focus();
}

onMounted(() => {
    recordRating();

    focusTheFirstFieldWithAnError();
});
</script>
